import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, GripVertical, Users, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  fetchAllDirectorsAdmin,
  createDirector,
  updateDirector,
  deleteDirector,
} from "@/lib/api";
import { useSiteContent } from "@/lib/SiteContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = () => ({
  name: "",
  role: "",
  shortBio: "",
  longBio: "",
  photoUrl: "",
  order: 99,
  isActive: true,
  social: { linkedin: "", instagram: "", facebook: "", twitter: "", email: "" },
});

const DirectorEditor = ({ value, onChange, onSave, onDelete, saving }) => {
  const update = (k, v) => onChange({ ...value, [k]: v });
  const updateSocial = (k, v) => onChange({ ...value, social: { ...(value.social || {}), [k]: v } });
  return (
    <div className="glass-card neon-border p-6 md:p-7 rounded-[18px] border" data-testid={`director-editor-${value.id || 'new'}`}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.45)] bg-[color:var(--sp-gold-soft)]">
            <Users className="h-4 w-4 text-[color:var(--sp-gold)]" />
          </span>
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">Director profile</p>
            <p className="text-sm text-white/80">{value.name || "Untitled director"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => update("isActive", !value.isActive)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/12 text-[10px] tracking-[0.22em] uppercase text-white/75 hover:text-[color:var(--sp-gold)] transition-colors"
            title="Toggle visibility on About page"
          >
            {value.isActive ? <Power className="h-3 w-3" /> : <PowerOff className="h-3 w-3" />}
            {value.isActive ? "Visible" : "Hidden"}
          </button>
          {value.id && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/15 transition-colors" title="Delete director">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#121212] border border-white/12 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this director?</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/65">
                    This will permanently remove {value.name || 'this profile'} from the About page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-white/5 border-white/15 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-red-500/90 text-white hover:bg-red-500">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn-gold neon-button inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.22em] uppercase disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? "Saving" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Name *</span>
          <input value={value.name} onChange={(e) => update("name", e.target.value)} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Role *</span>
          <input value={value.role} onChange={(e) => update("role", e.target.value)} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Short Bio (1–2 lines) *</span>
          <textarea rows={2} value={value.shortBio} onChange={(e) => update("shortBio", e.target.value)} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Long Bio (4–5 paragraphs, separate with a blank line) *</span>
          <textarea rows={9} value={value.longBio} onChange={(e) => update("longBio", e.target.value)} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Photo URL override (optional)</span>
          <input value={value.photoUrl || ""} onChange={(e) => update("photoUrl", e.target.value)} placeholder="Leave empty to use Media Manager photo" className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">Display Order</span>
          <input type="number" value={value.order || 0} onChange={(e) => update("order", parseInt(e.target.value || "0", 10))} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
        </label>
      </div>

      <p className="mt-7 text-[11px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)]">Social links</p>
      <div className="grid md:grid-cols-3 gap-4 mt-3">
        {["linkedin", "instagram", "facebook", "twitter", "email"].map((k) => (
          <label className="block" key={k}>
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55 capitalize">{k}</span>
            <input value={value.social?.[k] || ""} onChange={(e) => updateSocial(k, e.target.value)} placeholder={k === "email" ? "name@domain.com" : "https://…"} className="input-luxe mt-2 w-full px-4 py-3 text-sm" />
          </label>
        ))}
      </div>
    </div>
  );
};

const AdminDirectorsPage = () => {
  const { refresh } = useSiteContent();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState(null); // for new

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchAllDirectorsAdmin();
      setList(d.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch {
      toast.error("Failed to load directors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateLocal = (idx, next) => {
    setList((prev) => prev.map((d, i) => (i === idx ? next : d)));
  };

  const saveOne = async (idx) => {
    const d = list[idx];
    setSaving(d.id);
    try {
      await updateDirector(d.id, {
        name: d.name,
        role: d.role,
        shortBio: d.shortBio,
        longBio: d.longBio,
        photoUrl: d.photoUrl || null,
        order: d.order,
        isActive: d.isActive,
        social: d.social,
      });
      await Promise.all([load(), refresh()]);
      toast.success("Director saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Save failed.", { description: detail || "" });
    } finally {
      setSaving(null);
    }
  };

  const deleteOne = async (idx) => {
    const d = list[idx];
    try {
      await deleteDirector(d.id);
      await Promise.all([load(), refresh()]);
      toast.success("Director deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const createOne = async () => {
    if (!draft) return;
    setSaving("__new__");
    try {
      await createDirector({
        name: draft.name,
        role: draft.role,
        shortBio: draft.shortBio,
        longBio: draft.longBio,
        photoUrl: draft.photoUrl || null,
        order: draft.order || list.length + 1,
        isActive: !!draft.isActive,
        social: draft.social,
      });
      setDraft(null);
      await Promise.all([load(), refresh()]);
      toast.success("Director added.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Create failed.", { description: typeof detail === "string" ? detail : "Please check the required fields." });
    } finally {
      setSaving(null);
    }
  };

  return (
    <AdminLayout
      eyebrow="Directors"
      title="Manage Director Profiles"
      description="Edit names, roles, bios, and social links. Active profiles appear on the public About page in the chosen display order."
      actions={
        <button
          onClick={() => setDraft(empty())}
          data-testid="admin-director-add-button"
          className="btn-gold neon-button inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase"
        >
          <Plus className="h-3.5 w-3.5" /> Add director
        </button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-3 text-white/65"><Loader2 className="h-4 w-4 animate-spin text-[color:var(--sp-gold)]" /> Loading…</div>
      ) : (
        <div className="space-y-6">
          {draft && (
            <div className="glass-card neon-border-emerald p-6 md:p-7 rounded-[18px] border">
              <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-emerald)] mb-4"><span className="gold-divider" /> Adding new director</p>
              <DirectorEditor
                value={draft}
                onChange={setDraft}
                onSave={createOne}
                onDelete={() => setDraft(null)}
                saving={saving === "__new__"}
              />
              <div className="mt-3 text-right">
                <button onClick={() => setDraft(null)} className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white">Cancel</button>
              </div>
            </div>
          )}
          {list.length === 0 && !draft ? (
            <div className="glass-card p-10 rounded-[18px] text-center">
              <Users className="h-8 w-8 text-[color:var(--sp-gold)] mx-auto" />
              <p className="font-display text-2xl mt-4">No directors yet.</p>
              <p className="mt-2 text-white/55 text-sm">Use the “Add director” button above to create your first profile.</p>
            </div>
          ) : (
            list.map((d, i) => (
              <DirectorEditor
                key={d.id}
                value={d}
                onChange={(next) => updateLocal(i, next)}
                onSave={() => saveOne(i)}
                onDelete={() => deleteOne(i)}
                saving={saving === d.id}
              />
            ))
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDirectorsPage;
